/**
 * Authentication Service for QA AI Agent
 *
 * Handles authentication flow with the Identity Service:
 * - Session validation via cookies
 * - Redirect to Identity Service for login
 * - Logout handling
 */

import { User } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8003/api/v1';

/**
 * Public Identity Service login page. This is the last-resort fallback when
 * neither the backend's runtime config nor the build-time VITE var yields a
 * usable URL. It must be a public hostname: an internal dev hostname here
 * (identity.qa-platform.local) sent every signed-out visitor of
 * qa.provalab.io to a host that does not resolve on the internet.
 */
export const DEFAULT_IDENTITY_LOGIN_URL = 'https://auth.provalab.io/login';
const BUILD_TIME_LOGIN_URL: string = import.meta.env.VITE_IDENTITY_LOGIN_URL || '';

const LOCAL_HOST_SUFFIXES = ['.local', '.localhost', '.internal', '.svc', '.cluster.local'];
const PRIVATE_IPV4 = /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/;

/** True for hostnames that only resolve on a dev box / inside the cluster. */
export const isInternalHostname = (hostname: string): boolean => {
  const h = (hostname || '').toLowerCase();
  if (!h) return true;
  if (h === 'localhost' || h === '0.0.0.0' || h === '::1' || h === '[::1]') return true;
  if (PRIVATE_IPV4.test(h)) return true;
  return LOCAL_HOST_SUFFIXES.some((suffix) => h.endsWith(suffix));
};

/**
 * A login URL is usable only if the browser can reach its host: a public
 * host always is; an internal host is reachable only when the page itself is
 * being served from an internal host (local dev / in-cluster ingress).
 */
export const isReachableLoginUrl = (
  candidate: string | null | undefined,
  pageHostname: string,
): boolean => {
  if (!candidate) return false;
  let hostname: string;
  try {
    hostname = new URL(candidate).hostname;
  } catch {
    return false;
  }
  if (!hostname) return false;
  return !isInternalHostname(hostname) || isInternalHostname(pageHostname);
};

/** Append/replace `return_url` on a login base URL. */
export const buildLoginUrl = (base: string, returnUrl: string): string => {
  try {
    const url = new URL(base);
    url.searchParams.set('return_url', returnUrl);
    return url.toString();
  } catch {
    return `${base}?return_url=${encodeURIComponent(returnUrl)}`;
  }
};

/**
 * Pick the login redirect target, in priority order:
 *   1. the backend's runtime-configured URL (IDENTITY_SERVICE_LOGIN_URL),
 *   2. the build-time VITE_IDENTITY_LOGIN_URL,
 *   3. the public default.
 * A candidate whose host the browser cannot reach from this page is skipped.
 */
export const pickLoginRedirect = (
  runtimeLoginUrl: string | null | undefined,
  returnUrl: string,
  pageHostname: string,
  buildTimeLoginUrl: string = BUILD_TIME_LOGIN_URL,
): string => {
  if (isReachableLoginUrl(runtimeLoginUrl, pageHostname)) {
    const runtime = new URL(runtimeLoginUrl as string);
    return runtime.searchParams.has('return_url')
      ? runtime.toString()
      : buildLoginUrl(runtime.toString(), returnUrl);
  }
  const base = isReachableLoginUrl(buildTimeLoginUrl, pageHostname)
    ? buildTimeLoginUrl
    : DEFAULT_IDENTITY_LOGIN_URL;
  return buildLoginUrl(base, returnUrl);
};

/** Ask the backend for its runtime-configured Identity login URL. */
const fetchRuntimeLoginUrl = async (returnUrl: string): Promise<string | null> => {
  try {
    const response = await fetch(
      `${API_URL}/auth/login-url?return_url=${encodeURIComponent(returnUrl)}`,
      { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } },
    );
    if (!response.ok) return null;
    const data = await response.json();
    return typeof data?.login_url === 'string' ? data.login_url : null;
  } catch (error) {
    console.warn('[QA Agent Auth] runtime login-url lookup failed, using fallback', error);
    return null;
  }
};

// ProtectedRoute calls login() on every render while unauthenticated; only
// start one redirect.
let redirectInFlight = false;

// Debug logging
const DEBUG = true;
const log = (msg: string, data?: any) => {
  if (DEBUG) {
    console.log(`[QA Agent Auth] ${msg}`, data || '');
  }
};

/**
 * How long the session check may take before the page stops waiting. The
 * check gates every protected route: without a bound, a stalled request
 * leaves the app on "Loading..." indefinitely with nothing to act on.
 */
export const SESSION_CHECK_TIMEOUT_MS = 15_000;

/** Thrown when the session could not be checked at all (as opposed to a
 * definite "not signed in"), so callers can show a retryable error instead
 * of redirecting to login or spinning forever. */
export class SessionCheckError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionCheckError';
  }
}

/**
 * Get the current user session info from the backend
 * The backend will validate the session cookie with the Identity Service
 *
 * Resolves to null when the user is definitely not signed in (401), and
 * rejects with SessionCheckError when the backend could not be reached,
 * answered with a server error, or did not answer within
 * SESSION_CHECK_TIMEOUT_MS.
 */
export const getSessionInfo = async (): Promise<User | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SESSION_CHECK_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_URL}/auth/session`, {
      method: 'GET',
      credentials: 'include', // Include cookies
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    if (response.status === 401) {
      // Not authenticated
      return null;
    }

    if (!response.ok) {
      console.error('Session check failed:', response.statusText);
      throw new SessionCheckError(`Session check failed (${response.status})`);
    }

    const data = await response.json();
    return data.user as User;
  } catch (error) {
    if (error instanceof SessionCheckError) throw error;
    console.error('Error checking session:', error);
    const aborted = (error as { name?: string })?.name === 'AbortError';
    throw new SessionCheckError(
      aborted
        ? `The server did not answer the session check within ${SESSION_CHECK_TIMEOUT_MS / 1000}s`
        : 'Could not reach the server to check your session',
    );
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Redirect to Identity Service login page
 */
export const redirectToLogin = (returnUrl?: string): void => {
  if (redirectInFlight) return;
  redirectInFlight = true;
  const currentUrl = returnUrl || window.location.href;
  void (async () => {
    const runtimeLoginUrl = await fetchRuntimeLoginUrl(currentUrl);
    const loginUrl = pickLoginRedirect(runtimeLoginUrl, currentUrl, window.location.hostname);
    log('redirectToLogin', { returnUrl, currentUrl, runtimeLoginUrl, loginUrl });
    window.location.href = loginUrl;
    // If the navigation is abandoned (back button / bfcache restore), allow a
    // fresh attempt from the LoginPage button.
    setTimeout(() => { redirectInFlight = false; }, 5000);
  })();
};

/**
 * Logout - clear session and redirect to login
 */
export const logout = async (): Promise<void> => {
  try {
    // Call backend logout endpoint (will invalidate session with Identity Service)
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error('Logout error:', error);
  }

  // Redirect to login page
  redirectToLogin();
};

/**
 * Check authentication status
 *
 * Note: We don't check for cookie existence client-side because the session
 * cookie is HttpOnly (for security). Instead, we always validate with the
 * backend, which can see the cookie in the request.
 */
export const checkAuth = async (): Promise<{ isAuthenticated: boolean; user: User | null }> => {
  log('checkAuth starting - validating with backend...');

  // Always validate with backend - don't rely on client-side cookie check
  // because the session cookie is HttpOnly (not accessible via JavaScript)
  const user = await getSessionInfo();
  log('checkAuth: Session validation result', { user });

  return {
    isAuthenticated: user !== null,
    user,
  };
};

const authService = {
  getSessionInfo,
  redirectToLogin,
  logout,
  checkAuth,
};

export default authService;
